import clsx from 'clsx';
import React, { TextareaHTMLAttributes, useCallback, useEffect, useRef } from 'react';

import { FormError } from '../FormElements/FormElements';

import styles from './TextArea.module.scss';

type TextareaProps = {
  props: TextareaHTMLAttributes<HTMLTextAreaElement>;
  error?: string | React.ReactNode;
  isDisabled?: boolean;
  className?: string;
  autoExpand?: boolean;
  minRows?: number;
  maxRows?: number;
};

export const Textarea: React.FC<TextareaProps> = ({
  className,
  props,
  error,
  isDisabled,
  autoExpand = false,
  minRows = 3,
  maxRows,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const textareaProps = props as TextareaHTMLAttributes<HTMLTextAreaElement> & {
    ref?: React.Ref<HTMLTextAreaElement>;
  };
  const { ref: externalRef, onInput, rows, ...restProps } = textareaProps;

  const resizeTextarea = useCallback(
    (element: HTMLTextAreaElement | null) => {
      if (!autoExpand || !element) return;
      const target = element;

      target.style.height = 'auto';

      const lineHeight = parseFloat(window.getComputedStyle(target).lineHeight || '24');
      const minHeight = lineHeight * minRows;
      const maxHeight = maxRows ? lineHeight * maxRows : Number.POSITIVE_INFINITY;
      const targetHeight = Math.min(Math.max(target.scrollHeight, minHeight), maxHeight);

      target.style.height = `${targetHeight}px`;
      target.style.overflowY = target.scrollHeight > maxHeight ? 'auto' : 'hidden';
    },
    [autoExpand, minRows, maxRows],
  );

  useEffect(() => {
    resizeTextarea(textareaRef.current);
  }, [resizeTextarea, props.value, props.defaultValue]);

  return (
    <div className={clsx(styles.inputContainer, className, { [styles.error]: error })}>
      <div className={clsx(styles.textAreaBox, { [styles.disabled]: isDisabled })}>
        <textarea
          rows={rows ?? (autoExpand ? minRows : 7)}
          className={clsx(styles.textarea, { [styles.autoExpand]: autoExpand, [styles.disabled]: isDisabled })}
          {...restProps}
          ref={element => {
            textareaRef.current = element;

            if (typeof externalRef === 'function') {
              externalRef(element);
            } else if (externalRef && 'current' in externalRef) {
              (externalRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element;
            }
          }}
          onInput={event => {
            resizeTextarea(event.currentTarget);
            onInput?.(event);
          }}
          disabled={isDisabled}
        />
      </div>
      {error && <FormError error={error} />}
    </div>
  );
};
